// centerlines.js — one line per wall instead of one per room face.
//
// Rooms trace the FACES of their walls, so a wall between two rooms comes out twice (once from each side).
// For a takeoff that's wrong for everything that's one per wall — studs, track, insulation — and it makes
// every shared wall two lines to click. Here every room edge (and every open-area face) is projected onto
// its wall's centreline, the stretches are unioned per wall, and the result is chained into polylines:
// one line per wall type, running through corners, windows and doors.
//
//   centerlineChains({ edges, runs, ftPerUnit, doorMode, doorMaxOpeningFt })
//     edges: room edges after assignTags ({a, b, kind: wall|window|door|opening, run, tag, lf}) — from ALL
//            rooms on the sheet, plus open-area faces ({a, b, kind:"wall", run, tag})
//     runs:  engine runs ({ux, uy, nx, ny, c, th, t0, t1}) sheet units
//   → [{ tag, pts, lf, run, runLf, windows:[lf], windowSegs:[[a,b]], doors:[lf], doorSegs:[[a,b]], loose }]
//     (loose: a face line kept as drawn because it couldn't be tied to a wall)
//
// Doors: the line crosses them (board runs over the header) and the door is deducted like a window —
// width × door height. doorMode "gap" leaves them out instead. A plain opening (no swing) is crossed the same
// way only when it's door-sized; wider gaps (open plan, corridors) stay gaps. When the door swings drawn on the
// sheet are given (swings: engine doors [{hx,hy,cx,cy}]), a door-sized opening is only a door if a swing is
// drawn at it — a room outline crossing open floor (the side of a corridor, a gap under a tag) is not a door.

const EPS = 1e-6;

export const DOUBLE_DOOR_FT = 4.75; // a door opening this wide or wider is a pair (typically 6'-0" × 7'-0")

// doubtful: optional array — door-sized gaps left out as "not a door" are pushed here ({a, b, lf}) so the caller can
// have them checked (AI / estimator) instead of losing a door whose swing the engine didn't read
export function centerlineChains({
  edges,
  runs,
  ftPerUnit,
  doorMode = "deduct",
  doorMaxOpeningFt = 4.5,
  swings = null,
  doubtful = null,
}) {
  const U = (inches) => inches / 12 / ftPerUnit;
  const segDist0 = (p, a, b) => {
    const dx = b[0] - a[0],
      dy = b[1] - a[1],
      L2 = dx * dx + dy * dy || 1e-12;
    const t = Math.max(
      0,
      Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2),
    );
    return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
  };
  // a swing spans this opening: its hinge AND the end of its leaf (closed, or as drawn) both sit on it
  const spans = (d, e) => {
    if (segDist0([d.hx, d.hy], e.a, e.b) > U(12)) return false;
    const ends = [[d.cx, d.cy]];
    const l = d.leafLine;
    if (l)
      ends.push(
        Math.hypot(l.x1 - d.hx, l.y1 - d.hy) >
          Math.hypot(l.x2 - d.hx, l.y2 - d.hy)
          ? [l.x1, l.y1]
          : [l.x2, l.y2],
      );
    return ends.some((q) => segDist0(q, e.a, e.b) <= U(12));
  };
  const swingAt = (e) => (swings || []).some((d) => spans(d, e));
  // swings already accounted for: they span some door / opening of the sheet
  const usedSwing = new Set();
  if (swings)
    for (const e of edges || [])
      if (e.kind === "door" || e.kind === "opening")
        for (const d of swings) if (spans(d, e)) usedSwing.add(d);
  // a door-sized gap with no swing of its own, beside a swing that is another opening's door → not a door
  const besideOtherDoor = (e) => {
    const nearBy = swings.filter(
      (d) => segDist0([d.hx, d.hy], e.a, e.b) <= U(60),
    );
    return nearBy.length > 0 && nearBy.every((d) => usedSwing.has(d));
  };
  const byRun = new Map(); // run → [{t0, t1, kind, tag}]
  const loose = [];

  // which run a run-less edge lies on (bridged doors and openings carry no run): a parallel run whose
  // centreline is within half its thickness (+ slack) and whose extent reaches the edge
  const findRun = (e) => {
    const L = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]);
    if (L < EPS) return -1;
    const dx = (e.b[0] - e.a[0]) / L,
      dy = (e.b[1] - e.a[1]) / L;
    const mx = (e.a[0] + e.b[0]) / 2,
      my = (e.a[1] + e.b[1]) / 2;
    let best = -1,
      bd = Infinity;
    runs.forEach((R, i) => {
      if (!R || Math.abs(dx * R.ux + dy * R.uy) < 0.995) return;
      const d = Math.abs(R.nx * mx + R.ny * my - R.c);
      if (d > (R.th || 0) / 2 + U(3)) return;
      const ta = R.ux * e.a[0] + R.uy * e.a[1],
        tb = R.ux * e.b[0] + R.uy * e.b[1];
      const gap = Math.max(0, Math.min(ta, tb) - R.t1, R.t0 - Math.max(ta, tb));
      if (gap > U(12 * 9)) return;
      if (d + gap < bd) {
        bd = d + gap;
        best = i;
      }
    });
    return best;
  };

  for (const e of edges || []) {
    let kind = e.kind || "wall",
      noSwing = false;
    const L = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]);
    if (L < EPS) continue;
    if (kind === "opening") {
      // door-sized gap with no swing drawn: treat as a door; wider: open plan → leave out
      if (L * ftPerUnit > doorMaxOpeningFt) continue;
      if (swings && !swingAt(e)) {
        // the open side next to a door that's already counted at its own opening: not a door
        if (besideOtherDoor(e)) {
          doubtful?.push({ a: e.a, b: e.b, lf: L * ftPerUnit });
          continue;
        }
        // no swing of its own: maybe a cased opening / sliding door — counted, flagged to check
        noSwing = true;
      }
      kind = "door";
    }
    // a "door" the room tracer bridged next to a swing that doesn't span it (the open end of a corridor beside a door)
    if (
      kind === "door" &&
      swings &&
      swings.length &&
      !swingAt(e) &&
      L * ftPerUnit <= doorMaxOpeningFt &&
      besideOtherDoor(e)
    ) {
      doubtful?.push({ a: e.a, b: e.b, lf: L * ftPerUnit });
      continue;
    }
    if (kind === "door" && doorMode === "gap") continue;
    let run = e.run >= 0 && runs[e.run] ? e.run : findRun(e);
    if (run < 0) {
      if (kind === "wall" || kind === "window") loose.push({ ...e, kind });
      continue;
    }
    const R = runs[run];
    const ta = R.ux * e.a[0] + R.uy * e.a[1],
      tb = R.ux * e.b[0] + R.uy * e.b[1];
    if (!byRun.has(run)) byRun.set(run, []);
    byRun.get(run).push({
      t0: Math.min(ta, tb),
      t1: Math.max(ta, tb),
      kind,
      tag: kind === "door" ? null : e.tag || null,
      wet: !!e.wet,
      noSwing,
    });
  }

  // ── per wall: union of what the rooms on either side drew, as elementary pieces
  const segs = []; // { run, tag, t0, t1, ops:[{kind,t0,t1}] }
  const join = U(2.5); // pieces closer than this are one stretch (face corners, rounding)
  for (const [run, list] of byRun) {
    const R = runs[run];
    const cuts = [...new Set(list.flatMap((s) => [s.t0, s.t1]))].sort(
      (p, q) => p - q,
    );
    const pieces = [];
    for (let i = 0; i + 1 < cuts.length; i++) {
      const a = cuts[i],
        b = cuts[i + 1];
      if (b - a < EPS) continue;
      const cov = list.filter((s) => s.t0 <= a + EPS && s.t1 >= b - EPS);
      if (!cov.length) {
        pieces.push(null);
        continue;
      }
      // a door or window seen from either side is one
      const kind = cov.some((s) => s.kind === "door")
        ? "door"
        : cov.some((s) => s.kind === "window")
          ? "window"
          : "wall";
      const tags = new Map();
      for (const s of cov)
        if (s.tag) tags.set(s.tag, (tags.get(s.tag) || 0) + 1);
      const tag =
        [...tags.entries()].sort((p, q) => q[1] - p[1])[0]?.[0] || null;
      // a wall with a washroom (wet room) on either side is a washroom wall
      pieces.push({
        t0: a,
        t1: b,
        kind,
        tag,
        wet: cov.some((s) => s.wet),
        noSwing:
          kind === "door" &&
          cov.filter((s) => s.kind === "door").every((s) => s.noSwing),
      });
    }
    // doors carry no tag: they take the wall's tag on either side
    const real = pieces.filter(Boolean);
    for (let i = 0; i < real.length; i++) {
      if (real[i].tag) continue;
      const prev = real
          .slice(0, i)
          .reverse()
          .find((p) => p.tag),
        next = real.slice(i + 1).find((p) => p.tag);
      real[i].tag =
        (prev && (!next || real[i].t0 - prev.t1 <= next.t0 - real[i].t1)
          ? prev.tag
          : next?.tag) || null;
    }
    // stretches: consecutive pieces (gaps < join) with the same tag; a stretch that is only a door is dropped
    let cur = null;
    const flush = () => {
      if (cur && cur.ops.some((o) => o.kind === "wall" || o.kind === "window"))
        segs.push(cur);
      cur = null;
    };
    for (const p of real) {
      if (
        cur &&
        (p.t0 - cur.t1 > join ||
          p.tag !== cur.tag ||
          (p.kind !== "door" && p.wet !== cur.wet))
      )
        flush();
      if (!cur)
        cur = {
          run,
          tag: p.tag,
          wet: p.wet,
          t0: p.t0,
          t1: p.t1,
          ops: [],
          th: R.th || 0,
        };
      cur.t1 = Math.max(cur.t1, p.t1);
      const last = cur.ops[cur.ops.length - 1];
      if (last && last.kind === p.kind && p.t0 - last.t1 <= join) {
        last.t1 = p.t1;
        last.noSwing = last.noSwing && p.noSwing;
      } else
        cur.ops.push({
          kind: p.kind,
          t0: p.t0,
          t1: p.t1,
          noSwing: !!p.noSwing,
        });
    }
    flush();
  }

  // ── geometry: centreline points; a stretch end that stops at a crossing wall runs on to that wall's
  //    centreline (corners and tees meet in one point instead of stopping at the face)
  const P = (R, t) => [R.ux * t + R.nx * R.c, R.uy * t + R.ny * R.c];
  for (const s of segs) {
    const R = runs[s.run];
    s.A = P(R, s.t0);
    s.B = P(R, s.t1);
  }
  const lineX = (R1, R2) => {
    // intersection of two centrelines → t along R1
    const den = R1.ux * R2.uy - R1.uy * R2.ux;
    if (Math.abs(den) < 0.08) return null;
    const p1 = [R1.nx * R1.c, R1.ny * R1.c],
      p2 = [R2.nx * R2.c, R2.ny * R2.c];
    const t = ((p2[0] - p1[0]) * R2.uy - (p2[1] - p1[1]) * R2.ux) / den;
    return t;
  };
  for (const s of segs) {
    const R = runs[s.run];
    for (const end of ["t0", "t1"]) {
      let best = null;
      for (const o of segs) {
        if (o === s || o.run === s.run) continue;
        const R2 = runs[o.run];
        const t = lineX(R, R2);
        if (t == null) continue;
        const reach = (o.th || 0) / 2 + (s.th || 0) / 2 + U(4);
        if (Math.abs(t - s[end]) > reach) continue;
        // the crossing point must be on (or just past) the other wall
        const X = P(R, t),
          t2 = R2.ux * X[0] + R2.uy * X[1];
        if (t2 < o.t0 - reach || t2 > o.t1 + reach) continue;
        if (!best || Math.abs(t - s[end]) < Math.abs(best - s[end])) best = t;
      }
      if (best != null) {
        if (end === "t0") {
          s.t0 = Math.min(best, s.t1 - EPS);
          s.A = P(R, s.t0);
          if (s.ops[0]) s.ops[0].t0 = Math.min(s.ops[0].t0, s.t0);
        } else {
          s.t1 = Math.max(best, s.t0 + EPS);
          s.B = P(R, s.t1);
          const lo = s.ops[s.ops.length - 1];
          if (lo) lo.t1 = Math.max(lo.t1, s.t1);
        }
      }
    }
  }

  // ── chain stretches of one tag that meet end to end (a node where exactly two of that tag's ends meet)
  const tol = U(1.5);
  const near = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]) <= tol * 1.5;
  const used = new Set();
  const ends = new Map(); // tag → [{s, end, p}]
  for (const s of segs)
    for (const [end, p] of [
      ["A", s.A],
      ["B", s.B],
    ]) {
      const k = `${s.tag || ""}|${s.wet ? 1 : 0}`; // one line = one type, washroom or not
      if (!ends.has(k)) ends.set(k, []);
      ends.get(k).push({ s, end, p });
    }
  const partners = (s, end) => {
    const p = s[end];
    const list = (ends.get(`${s.tag || ""}|${s.wet ? 1 : 0}`) || []).filter(
      (x) => x.s !== s && near(x.p, p),
    );
    return list.length === 1 ? list[0] : null; // exactly one other end there (a tee or cross breaks the line)
  };
  const opsOf = (s, forward) => {
    const R = runs[s.run];
    return s.ops
      .filter((o) => o.kind !== "wall")
      .map((o) => {
        const a = P(R, Math.max(o.t0, s.t0)),
          b = P(R, Math.min(o.t1, s.t1));
        return {
          kind: o.kind,
          lf:
            Math.max(0, Math.min(o.t1, s.t1) - Math.max(o.t0, s.t0)) *
            ftPerUnit,
          seg: forward ? [a, b] : [b, a],
          noSwing: !!o.noSwing,
        };
      })
      .filter((o) => o.lf >= 0.5);
  };
  const chains = [];
  for (const s0 of segs) {
    if (used.has(s0)) continue;
    // walk back to the start of this chain
    let start = s0,
      startEnd = "A";
    for (let g = 0; g < segs.length; g++) {
      const pr = partners(start, startEnd);
      if (!pr || used.has(pr.s) || pr.s === s0) break; // free end, or once round a closed loop
      start = pr.s;
      startEnd = pr.end === "A" ? "B" : "A";
    }
    // walk forward collecting pts
    const pts = [],
      ops = [],
      runLf = {};
    let s = start,
      inEnd = startEnd;
    let lf = 0;
    while (s && !used.has(s)) {
      used.add(s);
      const forward = inEnd === "A";
      const a = forward ? s.A : s.B,
        b = forward ? s.B : s.A;
      if (!pts.length) pts.push(a);
      else if (!near(pts[pts.length - 1], a)) pts.push(a);
      else pts[pts.length - 1] = a;
      pts.push(b);
      const len = (s.t1 - s.t0) * ftPerUnit;
      lf += len;
      runLf[s.run] = (runLf[s.run] || 0) + len;
      ops.push(...opsOf(s, forward));
      const nx = partners(s, forward ? "B" : "A");
      if (!nx || used.has(nx.s)) break;
      s = nx.s;
      inEnd = nx.end;
    }
    // close a loop exactly
    if (pts.length > 3 && near(pts[0], pts[pts.length - 1]))
      pts[pts.length - 1] = pts[0];
    let run = -1,
      bl = 0;
    for (const [k, v] of Object.entries(runLf))
      if (v > bl) {
        bl = v;
        run = Number(k);
      }
    const win = ops.filter((o) => o.kind === "window"),
      drs = ops.filter((o) => o.kind === "door");
    const th = Math.max(0, ...Object.keys(runLf).map((k) => runs[k]?.th || 0));
    chains.push({
      tag: s0.tag,
      wet: !!s0.wet,
      pts: simplify(pts),
      lf,
      run,
      runLf,
      th,
      windows: win.map((o) => o.lf),
      windowSegs: win.map((o) => o.seg),
      doors: drs.map((o) => o.lf),
      doorSegs: drs.map((o) => o.seg),
      doorKinds: drs.map((o) => (o.lf >= DOUBLE_DOOR_FT ? "double" : "single")),
      doorNoSwing: drs.map((o) => !!o.noSwing),
    }); // (a door-sized opening with no swing drawn: flagged to check)
  }

  // edges that couldn't be tied to a wall: kept as drawn (one line each, merged when they touch)
  for (const e of loose) {
    const lf = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]) * ftPerUnit;
    const last = chains[chains.length - 1];
    if (
      last?.loose &&
      last.tag === (e.tag || null) &&
      last.wet === !!e.wet &&
      near(last.pts[last.pts.length - 1], e.a)
    ) {
      last.pts.push(e.b);
      last.lf += lf;
      continue;
    }
    chains.push({
      tag: e.tag || null,
      wet: !!e.wet,
      pts: [e.a, e.b],
      lf,
      run: -1,
      runLf: {},
      windows: e.kind === "window" ? [lf] : [],
      windowSegs: e.kind === "window" ? [[e.a, e.b]] : [],
      doors: [],
      doorSegs: [],
      doorKinds: [],
      loose: true,
    });
  }
  return chains.filter((c) => c.lf >= 0.25);
}

// drop repeated and in-line points (a straight run is two points, however many pieces made it)
export function simplify(pts, eps = 1e-6) {
  const p = [];
  for (const q of pts)
    if (
      !p.length ||
      Math.hypot(q[0] - p[p.length - 1][0], q[1] - p[p.length - 1][1]) > eps
    )
      p.push(q);
  const closed =
    p.length > 3 &&
    Math.hypot(p[0][0] - p[p.length - 1][0], p[0][1] - p[p.length - 1][1]) <=
      eps;
  const out = [];
  for (let i = 0; i < p.length; i++) {
    if (i === 0 || i === p.length - 1) {
      out.push(p[i]);
      continue;
    }
    const a = out[out.length - 1],
      b = p[i],
      c = p[i + 1];
    const cr = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    const L = Math.hypot(c[0] - a[0], c[1] - a[1]) || 1;
    const dot = (b[0] - a[0]) * (c[0] - b[0]) + (b[1] - a[1]) * (c[1] - b[1]);
    if (Math.abs(cr) / L <= Math.max(eps, L * 1e-4) && dot >= 0) continue; // straight on
    out.push(b);
  }
  if (closed && out.length > 3) {
    // the start point of a loop may sit mid-side
    const a = out[out.length - 2],
      b = out[0],
      c = out[1];
    const cr = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    if (Math.abs(cr) / (Math.hypot(c[0] - a[0], c[1] - a[1]) || 1) <= 1e-4) {
      out.shift();
      out[out.length - 1] = out[0];
    }
  }
  return out;
}

// Outside corners (corner bead): every corner that sticks out into a room — a reflex corner of the room's
// outline (a pilaster, a bulkhead return, a wall that jogs, a core inside an open office) — plus both faces of a
// wall that ends at a wide opening (no door frame there). Door jambs are left out — the frame covers them, so
// no bead at a door: not the jambs, not the wall-face corners beside them, not the tip of a door leaf.
//   rooms: [{ ring, edges:[{a,b,kind}] }] sheet units → [[x,y]] (one per corner, deduplicated)
export function outsideCorners(
  rooms,
  {
    ftPerUnit,
    doorMaxOpeningFt = 4.5,
    minLegIn = 3,
    doors = [],
    doorSegs = [],
    lines = null,
  } = {},
) {
  const U = (inches) => inches / 12 / ftPerUnit;
  const out = [];
  // one corner per wall corner: the two faces' corners of one wall end / bend are a wall-thickness apart
  const add = (p) => {
    if (!out.some((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) < U(9)))
      out.push(p);
  };
  // only corners of WALLS: a corner must sit on a traced wall line (within half a thick wall) — the
  // corners of furniture, vending machines, millwork or fixtures a room outline wraps around don't count
  const onWall = (p) => {
    if (!lines) return true;
    // (a face corner is half the wall's thickness off its centreline — a wall end's corner ~0.7 of it)
    for (const ln of lines) {
      const pts = ln.pts || ln,
        tol = (ln.th ? ln.th * 0.75 : U(6)) + U(1);
      for (let i = 0; i + 1 < pts.length; i++) {
        const a = pts[i],
          b = pts[i + 1],
          vx = b[0] - a[0],
          vy = b[1] - a[1],
          L2 = vx * vx + vy * vy;
        const k = L2
          ? Math.max(
              0,
              Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2),
            )
          : 0;
        if (Math.hypot(a[0] + vx * k - p[0], a[1] + vy * k - p[1]) <= tol)
          return true;
      }
    }
    return false;
  };
  const near = (p, q, t) => Math.hypot(p[0] - q[0], p[1] - q[1]) <= t;
  for (const r of rooms || []) {
    const ring = r.ring || [];
    const n = ring.length;
    if (n < 4) continue;
    let A = 0;
    for (let i = 0; i < n; i++) {
      const p = ring[i],
        q = ring[(i + 1) % n];
      A += p[0] * q[1] - q[0] * p[1];
    }
    const sgn = Math.sign(A) || 1;
    // door / opening edges and keyhole seams on this outline
    const doorish = (r.edges || []).filter(
      (e) => e.kind === "door" || e.kind === "opening",
    );
    const isSeam = (a, b) => {
      for (let k = 0; k < n; k++) {
        const c = ring[k],
          d = ring[(k + 1) % n];
        if (near(c, b, 1e-6) && near(d, a, 1e-6)) return true;
      }
      return false;
    };
    for (let i = 0; i < n; i++) {
      const a = ring[(i - 1 + n) % n],
        v = ring[i],
        b = ring[(i + 1) % n];
      const l1 = Math.hypot(v[0] - a[0], v[1] - a[1]),
        l2 = Math.hypot(b[0] - v[0], b[1] - v[1]);
      if (l1 < U(minLegIn) || l2 < U(minLegIn)) continue;
      const cr = (v[0] - a[0]) * (b[1] - v[1]) - (v[1] - a[1]) * (b[0] - v[0]);
      if (Math.abs(cr) / (l1 * l2) < 0.3) continue; // (nearly) straight
      if (Math.sign(cr) === sgn) continue; // turning the room's way: an inside corner
      if (isSeam(a, v) || isSeam(v, b)) continue;
      // a jamb: the corner sits on a door / opening edge (jambs are added below, once per side)
      if (doorish.some((e) => near(e.a, v, U(6)) || near(e.b, v, U(6))))
        continue;
      // the tip of an open door leaf (drawn as a thin box beside the wall) isn't a wall corner
      if (
        doors.some((d) => {
          const dh = Math.hypot(v[0] - d.hx, v[1] - d.hy);
          return (
            Math.abs(dh - d.r) < U(6) &&
            dh > d.r * 0.5 &&
            !near(v, [d.cx, d.cy], U(6))
          );
        })
      )
        continue;
      if (doorSegs.some(([p, q]) => near(p, v, U(8)) || near(q, v, U(8))))
        continue;
      if (!onWall(v)) continue;
      add(v);
    }
    // a wall that stops at a wide opening: its end has a corner on this room's side
    // (door openings are handled below from the wall lines: one corner at each jamb)
    for (const e of doorish) {
      if (e.kind !== "opening") continue;
      if (
        Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]) * ftPerUnit <=
        doorMaxOpeningFt
      )
        continue;
      if (onWall(e.a)) add(e.a);
      if (onWall(e.b)) add(e.b);
    }
  }
  // doors: no corner bead at a door — drop any corner at (or within a wall thickness of) a jamb
  const jambs = doorSegs.flatMap(([p, q]) => [p, q]);
  return out.filter(
    (o) => !jambs.some((j) => Math.hypot(o[0] - j[0], o[1] - j[1]) < U(9)),
  );
}

// ── trace clean-up ────────────────────────────────────────────────────────────────────────────────────
const segDist = ([px, py], [ax, ay], [bx, by]) => {
  const dx = bx - ax,
    dy = by - ay,
    L2 = dx * dx + dy * dy || 1e-12;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L2));
  return Math.hypot(px - ax - t * dx, py - ay - t * dy);
};
// does segment p→q pass through segment a→b (a→b counted `tol` longer at both ends — the tick at a dimension's end)
const crosses = (p, q, a, b, tol = 0) => {
  const rx = q[0] - p[0],
    ry = q[1] - p[1],
    sx = b[0] - a[0],
    sy = b[1] - a[1];
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-9) return false;
  const t = ((a[0] - p[0]) * sy - (a[1] - p[1]) * sx) / den; // along p→q
  const u = ((a[0] - p[0]) * ry - (a[1] - p[1]) * rx) / den; // along a→b
  const sl = Math.hypot(sx, sy) || 1;
  return t > 0.02 && t < 0.98 && u >= -tol / sl && u <= 1 + tol / sl;
};

/**
 * Open-area wall faces (walls no closed room drew) are where most false positives come from. Keep one only if
 *  • it joins the traced walls — an end within ~18" of a room wall or of another kept face (lines floating
 *    off the building — a neighbour's outline, a site line — are dropped), and
 *  • it isn't a piece of a dimension string — short faces crossing a dimension line (the double ticks between
 *    dimensions) or lying along one are dropped.
 * faces: [{a, b, ...}] · roomEdges: [{a, b}] · dims: [{a, b, h}] (scaleCheck.dimensionLines) — sheet units
 */
export function filterOpenFaces(
  faces,
  { roomEdges = [], dims = [], ftPerUnit },
) {
  const U = (inches) => inches / 12 / ftPerUnit;
  const len = (f) => Math.hypot(f.b[0] - f.a[0], f.b[1] - f.a[1]);
  const onDim = (f) =>
    dims.some((d) => {
      const tol = Math.max(d.h || 0, U(4));
      // short faces crossing a dimension line (ticks) or ending on one (extension lines)
      if (
        len(f) < U(60) &&
        (crosses(f.a, f.b, d.a, d.b, tol) ||
          segDist(f.a, d.a, d.b) <= tol ||
          segDist(f.b, d.a, d.b) <= tol)
      )
        return true;
      // along a dimension line: both ends close to it
      return segDist(f.a, d.a, d.b) <= tol && segDist(f.b, d.a, d.b) <= tol;
    });
  let pool = faces.filter((f) => !onDim(f));
  const anchors = roomEdges.map((e) => [e.a, e.b]);
  // 18": a face often stops a wall-thickness short of the room wall it meets (the junction belongs to the room)
  const near = (p, segs) => segs.some(([a, b]) => segDist(p, a, b) <= U(18));
  const kept = [];
  for (let changed = true; changed; ) {
    changed = false;
    const rest = [];
    for (const f of pool) {
      if (near(f.a, anchors) || near(f.b, anchors)) {
        kept.push(f);
        anchors.push([f.a, f.b]);
        changed = true;
      } else rest.push(f);
    }
    pool = rest;
  }
  return kept;
}

/**
 * One line per wall: a line lying inside a longer, parallel line's wall (a dashed line drawn inside a wall,
 * a window sill, the outer face of an exterior wall) is the same wall twice — drop it.
 * chains: centerlineChains() output (pts, th, lf, …) — sheet units.
 */
export function dropNestedLines(chains, { ftPerUnit }) {
  const U = (inches) => inches / 12 / ftPerUnit;
  const segsOf = (c) =>
    c.pts
      .slice(1)
      .map((p, i) => [c.pts[i], p])
      .filter(([a, b]) => Math.hypot(b[0] - a[0], b[1] - a[1]) > 1e-6);
  const S = chains.map(segsOf);
  const lenOf = (c, i) =>
    S[i].reduce((n, [a, b]) => n + Math.hypot(b[0] - a[0], b[1] - a[1]), 0);
  const L = chains.map(lenOf);
  const order = chains.map((_, i) => i).sort((i, j) => L[j] - L[i]);
  const drop = new Set();
  for (const i of order) {
    if (drop.has(i) || L[i] <= 0) continue;
    let covered = 0;
    for (const [a, b] of S[i]) {
      const sl = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const ux = (b[0] - a[0]) / sl,
        uy = (b[1] - a[1]) / sl;
      const iv = []; // covered stretches of this segment, in its own [0, sl]
      for (const j of order) {
        if (j === i || drop.has(j) || L[j] < L[i]) continue;
        if (L[j] === L[i] && j > i) continue;
        const band = ((chains[i].th || 0) + (chains[j].th || 0)) / 2 + U(1.5);
        if (band > U(16)) continue;
        for (const [p, q] of S[j]) {
          const tl = Math.hypot(q[0] - p[0], q[1] - p[1]);
          const vx = (q[0] - p[0]) / tl,
            vy = (q[1] - p[1]) / tl;
          if (Math.abs(ux * vy - uy * vx) > 0.05) continue; // not parallel (~3°)
          const off = Math.abs((a[0] - p[0]) * -vy + (a[1] - p[1]) * vx); // distance between the two lines
          if (off > Math.max(band, U(3))) continue;
          const s0 = (p[0] - a[0]) * ux + (p[1] - a[1]) * uy,
            s1 = (q[0] - a[0]) * ux + (q[1] - a[1]) * uy;
          const lo = Math.max(0, Math.min(s0, s1)),
            hi = Math.min(sl, Math.max(s0, s1));
          if (hi > lo) iv.push([lo, hi]);
        }
      }
      iv.sort((x, y) => x[0] - y[0]);
      let end = -Infinity;
      for (const [lo, hi] of iv) {
        if (hi <= end) continue;
        covered += hi - Math.max(lo, end);
        end = hi;
      }
    }
    if (covered >= 0.8 * L[i]) drop.add(i);
  }
  return chains.filter((_, i) => !drop.has(i));
}

/**
 * An area's outline with as few corners as it needs: corners within `tol` of the straight line through their
 * neighbours (small jogs at wall ends and pilasters, pixel steps) are dropped. Corners that appear twice — the
 * seam where an outline wraps around a hole — are always kept, so holes stay holes.
 * ring: [[x,y],…] (open or closed) · tol: same units → [[x,y],…] open ring
 */
export function simplifyRing(ring, tol) {
  let pts = ring.slice();
  if (
    pts.length > 1 &&
    Math.hypot(
      pts[0][0] - pts[pts.length - 1][0],
      pts[0][1] - pts[pts.length - 1][1],
    ) < 1e-9
  )
    pts.pop();
  if (pts.length <= 4 || !(tol > 0)) return pts;
  const key = (p) => `${Math.round(p[0] * 100)},${Math.round(p[1] * 100)}`;
  const cnt = new Map();
  for (const p of pts) cnt.set(key(p), (cnt.get(key(p)) || 0) + 1);
  const pinned = pts.map((p) => cnt.get(key(p)) > 1);
  const dist = (p, a, b) => {
    const dx = b[0] - a[0],
      dy = b[1] - a[1],
      L2 = dx * dx + dy * dy;
    if (!L2) return Math.hypot(p[0] - a[0], p[1] - a[1]);
    const t = Math.max(
      0,
      Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2),
    );
    return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
  };
  // Douglas–Peucker between anchors: pinned corners, plus the two corners farthest apart (a closed ring needs two)
  let i0 = 0,
    i1 = 0,
    bd = -1;
  for (let i = 0; i < pts.length; i++) {
    const d = Math.hypot(pts[i][0] - pts[0][0], pts[i][1] - pts[0][1]);
    if (d > bd) {
      bd = d;
      i1 = i;
    }
  }
  const anchors = [
    ...new Set([
      i0,
      i1,
      ...pinned.map((v, i) => (v ? i : -1)).filter((i) => i >= 0),
    ]),
  ].sort((a, b) => a - b);
  const keep = new Uint8Array(pts.length);
  for (const a of anchors) keep[a] = 1;
  const dp = (a, b) => {
    // indices along the ring (b may wrap past the end)
    const n = pts.length;
    let far = -1,
      fd = tol;
    for (let k = a + 1; k < b; k++) {
      const d = dist(pts[k % n], pts[a % n], pts[b % n]);
      if (d > fd) {
        fd = d;
        far = k;
      }
    }
    if (far < 0) return;
    keep[far % n] = 1;
    dp(a, far);
    dp(far, b);
  };
  for (let j = 0; j < anchors.length; j++) {
    const a = anchors[j],
      b = j + 1 < anchors.length ? anchors[j + 1] : anchors[0] + pts.length;
    dp(a, b);
  }
  const out = pts.filter((_, i) => keep[i]);
  return out.length >= 3 ? out : pts;
}
